import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import type { ContactsController } from "../../../../api/src/contacts/contacts.controller";
import type {
  Contact,
  ContactType,
  EditablePerson,
  Gender,
  MaritalStatus,
  Prefix,
  Suffix,
} from "../../../../api/src/contacts/lib";
import type { SerializedObject, UnpackResponse } from "../../../../api/src/lib";
import {
  ApiError,
  deserializeDate,
  serverError,
  useAuthenticatedMutationNew,
} from "../../lib/api";
import { capitalize } from "../../lib/display";
import { deserializeNote } from "../../lib/notes";
import { useAuthenticatedFetchAllPages } from "../../lib/pagination";
import { deserializeTask, deserializeWorkflow } from "../../tasks/lib";

/**
 * The sentence the api answered a failed contact request with, or `null` when
 * there is none to show: the error did not come from a response, or the body
 * carried no `message`. Read through `serverError` (`lib/api.ts`), the one
 * reader of the api's error body (APP-1526).
 * @param err The value caught from a failed request
 */
export function contactErrorMessage(err: unknown): string | null {
  const answer = serverError(err);
  if (answer === null) return null;
  if (answer.body === null) return answer.text;
  const message = answer.body.message;
  return typeof message === "string" && message.length > 0 ? message : null;
}

/**
 * Whether a failed contact request was a 404: the contact does not exist, or
 * not in the caller's firm (APP-1625).
 * @param err The value caught from a failed request
 */
export function isContactNotFound(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

/**
 * React Query `retry` for the contact read. A 404 is an answer, not a
 * transient failure, so it is not retried; the React Query default of three
 * retries with backoff held the "not found" state back for seconds. Every
 * other failure keeps that default.
 * @param failureCount How many times the query has failed so far
 * @param err The value the last attempt failed with
 */
export function retryUnlessContactNotFound(
  failureCount: number,
  err: unknown,
): boolean {
  return !isContactNotFound(err) && failureCount < 3;
}

export const CONTACT_TYPES: ContactType[] = ["person", "trust", "organization"];

export const GENDERS: Gender[] = [
  "male",
  "female",
  "non-binary",
  "other",
  "unknown",
];

export const MARITAL_STATUSES: MaritalStatus[] = [
  "single",
  "married",
  "divorced",
  "widowed",
  "life partner",
  "separated",
  "unknown",
];

export const HOUSEHOLD_TITLES = ["head", "spouse", "dependent", "other"];
export const HOUSEHOLD_TITLES_OPTIONS = HOUSEHOLD_TITLES.map(
  (title, index) => ({
    label: capitalize(title),
    value: index + 1,
  }),
);

export const PREFIXES: Prefix[] = [
  "Dr.",
  "Dr. & Mr.",
  "Dr. & Mrs.",
  "Drs.",
  "Father",
  "Judge",
  "Maj.",
  "Miss",
  "Mr.",
  "Mr. & Mrs.",
  "Mrs.",
  "Ms.",
  "Pastor",
  "Pfc.",
  "Prof.",
  "Rev.",
  "Sgt.",
  "Sister",
];

export const SUFFIXES: Suffix[] = ["II", "III", "IV", "V", "Jr.", "Sr."];

export function deserializeContact(
  contact: SerializedObject<Contact>,
): Contact {
  return {
    ...contact,
    birthDate: deserializeDate(contact.birthDate),
    clientSince: deserializeDate(contact.clientSince),
    anniversary: deserializeDate(contact.anniversary),
    dateOfDeath: deserializeDate(contact.dateOfDeath),
    retirementDate: deserializeDate(contact.retirementDate),
    archiveDate: deserializeDate(contact.archiveDate),
    notes: contact.notes.map(deserializeNote),
    tasks: contact.tasks.map(deserializeTask),
    workflows: contact.workflows.map(deserializeWorkflow),
  };
}

/**
 * A `Contact` as the default values of the edit forms (`PersonForm`,
 * `TrustForm`). `Contact.streetAddress` and `.job` are `T | null` (APP-1653),
 * while the `PUT /contacts/:id` body takes each as an optional object
 * (`EditablePerson`), so a `null` becomes an absent key: the save then leaves
 * the stored value alone.
 * @param contact The contact as read from the api
 */
export function contactFormValues<T extends Contact>(contact: T) {
  return {
    ...contact,
    streetAddress: contact.streetAddress ?? undefined,
    job: contact.job ?? undefined,
  };
}

/**
 * The keys of the `PUT /v1/contacts/:id` body: the keys `UpdateContactDto`
 * (`api/src/contacts/contacts.dto.ts`) declares, which are the keys of
 * `EditablePerson`. A `Record` keyed by that type, so `tsc` refuses a key
 * added to or removed from it until this list follows;
 * `lib.update-contact.spec.tsx` reads the class and holds the two together.
 */
export const UPDATE_CONTACT_KEYS = Object.keys({
  name: true,
  prefix: true,
  givenName: true,
  familyName: true,
  additionalName: true,
  suffix: true,
  title: true,
  birthDate: true,
  email: true,
  phoneNumber: true,
  gender: true,
  maritalStatus: true,
  dateOfDeath: true,
  retirementDate: true,
  anniversary: true,
  clientSince: true,
  backgroundInfo: true,
  nickName: true,
  streetAddress: true,
  job: true,
} satisfies Record<keyof EditablePerson, true>) as (keyof EditablePerson)[];

/**
 * The keys whose cleared value the form casts to `undefined`, which
 * `JSON.stringify` drops; `contactUpdateBody` sends them as `null` (R99,
 * APP-2061). yup's object cast leaves out a key whose field casts to
 * `undefined` (yup 1.7.1, `ObjectSchema._cast`), so the values `yupResolver`
 * hands an edit form's submit do not hold a cleared key at all: the form
 * passes `clearAbsent` so an absent key is sent as `null` too.
 */
const CLEARABLE_KEYS: ReadonlySet<string> = new Set([
  "birthDate",
  "clientSince",
  "anniversary",
  "dateOfDeath",
  "retirementDate",
  "gender",
  "maritalStatus",
  "title",
]);

/**
 * The body of `PUT /v1/contacts/:id` for the values an edit form submits, or
 * for a contact with a field changed (`HouseholdRMD.tsx`): the keys of
 * `UPDATE_CONTACT_KEYS` those values hold, each as it is, except that a
 * cleared date or select field (`undefined`, or absent under `clearAbsent`) is
 * sent as `null`. The api refuses any
 * other key (APP-1653). The forms hold the whole contact
 * (`contactFormValues`), and before this they sent it whole; the api then
 * wrote `household` and `type` of the body to the contact and sent `isActive`
 * to the CRM as the record's status. Each of those now stays as it is on an
 * edit. For `type` that is a change: a contact stored as a trust or an
 * organization and linked to a Slant person is read as a person, and an edit
 * used to store that.
 * @param values The form's values, or a contact
 * @param options `clearAbsent`: the values are what `yupResolver` cast, so a
 * clearable key they do not hold was cleared, and is sent as `null`. A caller
 * holding a contact rather than form values leaves it off: there an absent
 * key means "leave this field alone".
 */
export function contactUpdateBody(
  values: object,
  { clearAbsent = false }: { clearAbsent?: boolean } = {},
): EditablePerson {
  const held = values as Record<string, unknown>;
  return Object.fromEntries(
    UPDATE_CONTACT_KEYS.filter(
      (key) => key in held || (clearAbsent && CLEARABLE_KEYS.has(key)),
    ).map((key) => [
      key,
      held[key] === undefined && CLEARABLE_KEYS.has(key) ? null : held[key],
    ]),
  ) as EditablePerson;
}

/**
 * The contact a form holds after `PUT /v1/contacts/:id` answers, in the shape
 * the api sends: what that response held before the body was the declared
 * keys only (APP-1653). The response then carried each other key as the form
 * had sent it, which was as the page held it: the tags, the status and the
 * links of the CRM record, which the stored contact does not hold. So each
 * key of `UPDATE_CONTACT_KEYS` and `id` come from the response, each other key
 * from the contact the page holds, and `image`, `additionalFields`,
 * `additionalFieldTypes` and `household` from the response where the page
 * holds `null`, as `ContactsService.update` chose them.
 * @param held The contact the page held when the form was submitted
 * @param updated `data` of the response
 */
export function contactAfterUpdate<T extends object>(
  held: T | undefined,
  updated: T,
): T {
  if (held === undefined) return updated;
  const before = held as Record<string, unknown>;
  const after = updated as Record<string, unknown>;
  return {
    ...held,
    ...Object.fromEntries(
      (UPDATE_CONTACT_KEYS as string[])
        .filter((key) => key in after)
        .map((key) => [key, after[key]]),
    ),
    ...Object.fromEntries(
      ["image", "additionalFields", "additionalFieldTypes", "household"].map(
        (key) => [key, before[key] ?? after[key]],
      ),
    ),
    id: after.id,
  } as T;
}

/**
 * The path of `getContacts`. Also the FIRST element of every react-query key
 * the page walk in `useQueryContacts` uses.
 */
export const CONTACTS_PATH = "/v1/contacts";

/** One item of `GET /contacts`, as the route serialises it. */
type SerializedContactListItem = SerializedObject<
  UnpackResponse<ContactsController["getContacts"]>
>["data"][number];

/**
 * Every contact of the caller's firm. `GET /contacts` answers a page
 * (APP-1653), and `Contacts.tsx` filters, sorts and pages the whole list in the
 * browser, so this walks every page (`useAuthenticatedFetchAllPages`) and holds
 * `data` back until the last one is in. Each record goes through
 * `deserializeContact`, as before.
 *
 * The cached pages are dropped when the hook unmounts, so the next mount walks
 * again. Before the route was paginated, `Contacts.tsx` read it through
 * `useAuthenticatedFetch`, which reads it again on every mount (the app's
 * `QueryClient` sets no `staleTime`, `index.tsx`). A finished walk holds no
 * observer on its first page, so without the drop a remount would be handed
 * the cached page and stop there (`removeModelListPages`, `models/lib.tsx`,
 * for the same reason). Every write that changes this list is made on a
 * screen that does not mount it: `useCreateContact`, `useArchiveContact`,
 * `useUnarchiveContact`, `PersonForm.tsx`, `TrustForm.tsx`,
 * `ContactMapping.tsx` and the household screens. Dropping the pages when the
 * list goes away covers each of them, and a change made in a CRM too.
 */
export function useQueryContacts() {
  const queryClient = useQueryClient();
  const { rows, hasEveryRow, isError } =
    useAuthenticatedFetchAllPages<SerializedContactListItem>(CONTACTS_PATH);

  useEffect(
    () => () => queryClient.removeQueries({ queryKey: [CONTACTS_PATH] }),
    [queryClient],
  );

  const data: Contact[] | undefined = useMemo(
    () => (hasEveryRow ? rows.map(deserializeContact) : undefined),
    [rows, hasEveryRow],
  );

  const isPending = !hasEveryRow && !isError;

  return { isPending, isError, data };
}

export function useCreateContact() {
  return useAuthenticatedMutationNew<
    UnpackResponse<ContactsController["createContact"]>,
    number,
    { type: ContactType; name: string }
  >({
    // `POST /contacts` is served at `/v1` only, and answers the created
    // contact where it answered the contact's ID (APP-1653, C5). The hook
    // still returns the ID, which is all `CreateContact.tsx` reads.
    mutationKey: ["POST", "contacts"],
    urlBuilderFn: (key) => `/v1/${key.join("/")}`,
    mutationFn: ({ data }: { data: { id: number } }) => data.id,
  });
}

export function useArchiveContact(archiveContactId: number) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<ContactsController["archiveContact"]>,
    void,
    void
  >({
    // `POST /contacts/:id/archive-date` is served at `/v1` only (APP-1653),
    // and answers the archived contact, which this hook does not read; the
    // contact it invalidates is read at `GET /v1/contacts/:id`
    // (`ContactInfo.tsx`).
    mutationKey: ["POST", "contacts", archiveContactId, "archive-date"],
    urlBuilderFn: (key) => `/v1/${key.join("/")}`,
    mutationFn: () => {
      queryClient.invalidateQueries({
        queryKey: [`/v1/contacts/${archiveContactId}`],
      });
    },
  });
}

export function useUnarchiveContact(archiveContactId: number) {
  const queryClient = useQueryClient();

  return useAuthenticatedMutationNew<
    UnpackResponse<ContactsController["unarchiveContact"]>,
    void,
    void
  >({
    // Under `/v1`, as `useArchiveContact` above.
    mutationKey: ["DELETE", "contacts", archiveContactId, "archive-date"],
    urlBuilderFn: (key) => `/v1/${key.join("/")}`,
    mutationFn: () => {
      queryClient.invalidateQueries({
        queryKey: [`/v1/contacts/${archiveContactId}`],
      });
    },
  });
}
